/**
 * The `Relay` interface and the tick scheduler both relays share.
 *
 * The simulation only ever advances by consuming an ordered command set for a
 * tick from a relay (KTD3). `LocalRelay` resolves in-process; `NetworkRelay`
 * waits on a Durable Object. Nothing else in the codebase knows which is
 * active, which is what makes every single-player test exercise the
 * multiplayer path.
 */

import {
  type Command,
  type CommandWorld,
  type Rejection,
  sortCommands,
  validate,
} from './commands.js'
import { INPUT_DELAY_TICKS, MAX_COMMANDS_PER_TICK, MAX_INPUT_DELAY_TICKS } from '../core/config.js'

export interface Relay {
  /** The slot this client plays. */
  readonly localSlot: number
  /** Ticks between submission and execution. */
  readonly delay: number

  /**
   * Offer a command for execution at `currentTick + delay`.
   * Returns the rejection if it was refused, or `null` if accepted.
   */
  submit(cmd: Omit<Command, 'slot' | 'seq'> & { slot?: number; seq?: number }): Rejection | null

  /** The committed set for `tick`, or `null` if it has not arrived. */
  commandsFor(tick: number): readonly Command[] | null

  /** Called by the engine after `tick` has executed. */
  onTickExecuted(tick: number, hash: number | null): void

  dispose(): void
}

/**
 * Per-tick command ring.
 *
 * Sized well past the delay window so a submission never wraps onto a tick that
 * is still pending, and every write is bounds-checked against the accepted
 * window rather than trusting the index.
 */
export class CommandRing {
  private readonly size: number
  private readonly slots: Command[][]
  /** Tick each slot currently holds, so a stale slot is never mistaken for data. */
  private readonly slotTick: Int32Array
  private readonly committed: Uint8Array

  constructor(size = MAX_INPUT_DELAY_TICKS * 8) {
    this.size = size
    this.slots = Array.from({ length: size }, () => [] as Command[])
    this.slotTick = new Int32Array(size).fill(-1)
    this.committed = new Uint8Array(size)
  }

  /** Highest tick this ring holds a committed set for. Diagnostics only. */
  highestCommitted = -1

  private index(tick: number): number {
    return ((tick % this.size) + this.size) % this.size
  }

  /** Prepare a slot for `tick`, clearing whatever it held for an older tick. */
  private ensure(tick: number): number {
    const i = this.index(tick)
    if (this.slotTick[i] !== tick) {
      this.slotTick[i] = tick
      this.slots[i].length = 0
      this.committed[i] = 0
    }
    return i
  }

  /** Number of commands already staged for `tick` from `slot`. */
  countFor(tick: number, slot: number): number {
    const i = this.index(tick)
    if (this.slotTick[i] !== tick) return 0
    let n = 0
    for (const c of this.slots[i]) if (c.slot === slot) n++
    return n
  }

  isCommitted(tick: number): boolean {
    const i = this.index(tick)
    return this.slotTick[i] === tick && this.committed[i] === 1
  }

  stage(tick: number, cmd: Command): void {
    const i = this.ensure(tick)
    this.slots[i].push(cmd)
  }

  /** Freeze a tick's set into its canonical order. Idempotent. */
  commit(tick: number): readonly Command[] {
    const i = this.ensure(tick)
    if (this.committed[i] === 0) {
      sortCommands(this.slots[i])
      this.committed[i] = 1
    }
    return this.slots[i]
  }

  /** The committed set, or `null` if this tick has not been committed. */
  get(tick: number): readonly Command[] | null {
    const i = this.index(tick)
    if (this.slotTick[i] !== tick || this.committed[i] === 0) return null
    return this.slots[i]
  }

  /** Replace a tick's set wholesale — how a network client applies a broadcast. */
  set(tick: number, commands: Command[]): void {
    if (tick > this.highestCommitted) this.highestCommitted = tick
    const i = this.ensure(tick)
    this.slots[i].length = 0
    for (const c of commands) this.slots[i].push(c)
    sortCommands(this.slots[i])
    this.committed[i] = 1
  }

  release(tick: number): void {
    const i = this.index(tick)
    if (this.slotTick[i] === tick) {
      this.slots[i].length = 0
      this.slotTick[i] = -1
      this.committed[i] = 0
    }
  }

  clear(): void {
    for (const s of this.slots) s.length = 0
    this.slotTick.fill(-1)
    this.committed.fill(0)
  }
}

export interface SchedulerOptions {
  localSlot: number
  world: CommandWorld
  delay?: number
  /** Commands one player may submit for one tick (R35). */
  maxPerTick?: number
}

/**
 * Shared submission bookkeeping: sequence numbers, the delay window, and
 * validation. Both relays own one.
 */
export class Scheduler {
  readonly ring = new CommandRing()
  localSlot: number
  readonly delay: number
  private world: CommandWorld
  private readonly maxPerTick: number
  private seqCounter = 0

  /** The last tick the engine executed. Submissions target `currentTick + delay`. */
  currentTick = -1

  constructor(opts: SchedulerOptions) {
    this.localSlot = opts.localSlot
    this.world = opts.world
    this.delay = opts.delay ?? INPUT_DELAY_TICKS
    this.maxPerTick = opts.maxPerTick ?? MAX_COMMANDS_PER_TICK
  }

  /**
   * Point the scheduler at the real simulation.
   *
   * A networked relay exists before the match does: it is what tells the client
   * which slot it is and what the seed is, so it is constructed against a
   * placeholder world that knows nothing. Leaving it there is not a small bug —
   * the placeholder reports every entity as owned by nobody, so the ownership
   * check refuses every order the player issues with "Not your unit".
   */
  attach(world: CommandWorld, localSlot: number): void {
    this.world = world
    this.localSlot = localSlot
  }

  /** The tick a command submitted now would execute at. */
  get targetTick(): number {
    return this.currentTick + 1 + this.delay
  }

  nextSeq(): number {
    return this.seqCounter++
  }

  /**
   * Validate an incoming command against the world and the delay window.
   * `senderSlot` is where the message actually came from, which is what makes
   * a spoofed `slot` field detectable.
   */
  check(cmd: Command, execTick: number, senderSlot: number): Rejection | null {
    const rejection = validate(cmd, {
      world: this.world,
      currentTick: this.currentTick,
      execTick,
      senderSlot,
      maxDelay: this.delay + MAX_INPUT_DELAY_TICKS,
    })
    if (rejection) return rejection
    if (this.ring.isCommitted(execTick)) {
      return { reason: 'bad-tick', detail: 'already committed' }
    }
    if (this.ring.countFor(execTick, cmd.slot) >= this.maxPerTick) {
      return { reason: 'too-many-units', detail: 'per-tick command cap' }
    }
    return null
  }
}
